import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { FormContainer, useForm } from 'react-hook-form-mui';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import { APIContext } from '../../Contexts/APIContext';
import ValidatedTextField from '../../Utilities/Forms/ValidatedTextField';
import EditOrgUserViewModel from './ViewModels/EditOrgUserViewModel';
import { useContext, useEffect, useRef, useState } from 'react';
import ValidatedSelectElement from '../../Utilities/Forms/ValidatedSelectElement';
import DropDownViewModel from '../../Utilities/ViewModels/DropDownViewModel';

interface IProps {
    open: boolean;
    onClose: (refresh: boolean) => void;
    userId: string | null;
    orgId: string;
    showRoles: boolean;
}

export default function EditOrgUserDialog(props: IProps) {
    const { get, post, showMessage, loading } = useContext(APIContext);
    const { open, onClose, userId, orgId, showRoles } = props;
    const [roles, setRoles] = useState<DropDownViewModel[]>([]);
    const [organisations, setOrganisations] = useState<DropDownViewModel[]>([]);
    const [sites, setSites] = useState<DropDownViewModel[]>([]);
    const [user, setUser] = useState<EditOrgUserViewModel>(new EditOrgUserViewModel());

    const formContext = useForm<EditOrgUserViewModel>({
        defaultValues: user,
    });


    const { reset, setValue, watch } = formContext;
    const selectedOrganisationId = watch('organisationId');
    const previousOrganisationId = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (user) {
            reset(user);
        }
    }, [user, reset]);

    useEffect(() => {
        if (!open) {
            previousOrganisationId.current = undefined;
        }
    }, [open]);

    useEffect(() => {
        if (userId && userId.length > 0) {
            getData();
        }
    }, [userId]);

    const getData = async () => {
        get("AdminUser", `GetUserDetails?id=${userId}`, (data) => {
            const loadedUser = data as EditOrgUserViewModel;
            setUser(loadedUser);

            const siteOrgId = loadedUser.organisationId || orgId;
            if (siteOrgId) {
                get("AdminUser", `GetSitesDropdown?orgId=${siteOrgId}`, setSites);
            } else {
                setSites([]);
            }
        });
        get("AdminUser", `GetRolesDropdown?level=org`, setRoles);
        get("AdminUser", `GetOrganisations`, setOrganisations);
    }

    useEffect(() => {
        const siteOrgId = selectedOrganisationId || orgId;
        if (!siteOrgId) {
            setSites([]);
            return;
        }

        if (previousOrganisationId.current !== undefined && selectedOrganisationId !== previousOrganisationId.current) {
            setValue('siteId', '');
        }

        previousOrganisationId.current = selectedOrganisationId;
        get("AdminUser", `GetSitesDropdown?orgId=${siteOrgId}`, setSites);
    }, [selectedOrganisationId, orgId, setValue]);

    const submit = async (form: any) => {
        await post("AdminUser", `EditUser`, form, checkCloseDialog);
    }

    const checkCloseDialog = async (success: boolean = true) => {
        reset(new EditOrgUserViewModel());
        onClose(success);
        if (success) {
            showMessage('success', 'User has been updated');
        }
    }

    return (
        <>
            {userId != null &&
                <Dialog
                    open={open}
                    onClose={() => checkCloseDialog(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <FormContainer
                        formContext={formContext}
                        onSuccess={submit}
                    >
                        <DialogTitle>{userId === '' ? "Create User" : "Edit User"}</DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <InputLabel shrink>Forename</InputLabel>
                                    <ValidatedTextField name="firstName" disabled={loading} />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <InputLabel shrink>Surname</InputLabel>
                                    <ValidatedTextField name="lastName" disabled={loading} />
                                </Grid>
                                {showRoles &&
                                    <Grid size={{ xs: 12 }}>
                                        <InputLabel shrink>Organisation</InputLabel>
                                        <ValidatedSelectElement name="organisationId" options={organisations} disabled={loading} value={user.organisationId} />
                                    </Grid>
                                }
                                <Grid size={{ xs: 12 }}>
                                    <InputLabel shrink>Site</InputLabel>
                                    <ValidatedSelectElement name="siteId" options={sites} disabled={loading} value={user.siteId} />
                                </Grid>
                                {showRoles &&
                                    <Grid size={{ xs: 12 }}>
                                        <InputLabel shrink>Role</InputLabel>
                                        <ValidatedSelectElement name="roleId" options={roles} disabled={loading} value={user.roleId} />
                                    </Grid>
                                }


                            </Grid>
                            <DialogActions>
                                <Button disabled={loading} variant="contained" type="submit">{userId ? "Update" : "Add"}</Button>
                                <Button disabled={loading} onClick={() => checkCloseDialog(false)} color="error">Cancel</Button>
                            </DialogActions>
                        </DialogContent>
                    </FormContainer>
                </Dialog>
            }
        </>

    );
}
